import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LoginForm from '../organisms/LoginForm';
import RegisterForm from '../organisms/RegisterForm';
import { useAuth } from '../contexts/AuthContext';

/**
 * Entrar e cadastrar no mesmo cartão: o painel roxo desliza sobre o formulário
 * inativo e revela o outro. Eram duas páginas, e trocar entre elas recarregava
 * a tela inteira para mostrar um formulário com os mesmos campos.
 *
 * As duas rotas continuam existindo (/entrar e /cadastro), porque é para elas
 * que o app redireciona e é o que os usuários têm salvo. O modo nasce do
 * caminho e volta para ele ao alternar — sem isso, atualizar a página no meio
 * do cadastro jogaria o usuário de volta no login.
 */
function AuthPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, register } = useAuth();

  const modeFromPath = location.pathname === '/cadastro' ? 'signup' : 'signin';
  const [mode, setMode] = useState(modeFromPath);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cobre o botão "voltar" do navegador e o caso de a rota mudar por fora.
  useEffect(() => {
    setMode(modeFromPath);
  }, [modeFromPath]);

  function switchTo(next) {
    if (next === mode) return;
    // O erro é do formulário que está saindo de cena; mantê-lo deixaria um
    // alerta órfão em cima do outro.
    setError(null);
    setMode(next);
    navigate(next === 'signup' ? '/cadastro' : '/entrar', { replace: true });
  }

  async function run(action) {
    setError(null);
    setLoading(true);
    try {
      return await action();
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }

  async function handleLogin({ email, password }) {
    const ok = await run(async () => { await login(email, password); return true; });
    if (ok) navigate('/pedidos');
  }

  async function handleRegister(data) {
    const ok = await run(async () => { await register(data); return true; });
    if (ok) navigate('/pedidos');
  }

  const isSignup = mode === 'signup';

  return (
    <div className="auth-shell d-flex justify-content-center align-items-center p-3 p-md-4">
      {/* Formas soltas no fundo da página, atrás do cartão — o papel de parede
          da tela, não decoração do formulário. Sangram pelas bordas de
          propósito: recortadas pela janela elas parecem parte de um desenho
          maior, enquanto contidas na tela virariam figuras avulsas.
          Em SVG porque são orgânicas: com border-radius de elipse não se
          chega a uma silhueta assimétrica convincente. */}
      <svg
        className="auth-bg-art d-none d-md-block"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
        focusable="false"
      >
        <path
          className="auth-bg-blob"
          d="M-160-140C60-170 300-40 330 140 358 310 210 420 40 396-130 372-260 240-240 60Z"
        />
        <path
          className="auth-bg-blob auth-bg-blob-light"
          d="M1600 1040C1380 1070 1140 940 1110 760 1082 590 1230 480 1400 504 1570 528 1700 660 1680 840Z"
        />
        {/* Peças pequenas: quebram a simetria dos dois cantos e dão o segundo
            tom da marca longe do painel. */}
        <rect className="auth-bg-chip" x="1156" y="96" width="34" height="34" rx="7" transform="rotate(28 1173 113)" />
        <rect className="auth-bg-ring" x="196" y="742" width="44" height="44" rx="9" transform="rotate(18 218 764)" />
        <circle className="auth-bg-dot" cx="1298" cy="286" r="9" />
        <circle className="auth-bg-dot auth-bg-dot-amber" cx="120" cy="560" r="7" />
      </svg>

      {/* O mesmo papel de parede recomposto para retrato. Não dá para reusar o
          de cima: com `slice` num celular em pé, o recorte come as laterais do
          viewBox deitado e sobra justamente o miolo, que é onde não há nada —
          as bolhas dos cantos ficariam todas fora da tela. */}
      <svg
        className="auth-bg-art d-md-none"
        viewBox="0 0 420 900"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
        focusable="false"
      >
        <path
          className="auth-bg-blob"
          d="M-140-60C-20-140 180-80 205 60 228 190 120 268-10 250-140 232-215 120-190 20Z"
        />
        <path
          className="auth-bg-blob auth-bg-blob-light"
          d="M560 980C430 1030 250 950 232 820 214 690 330 610 460 630 590 650 660 770 640 870Z"
        />
        {/* As peças pequenas vivem nas faixas acima e abaixo do cartão: no
            celular ele ocupa quase toda a largura, e no meio da tela qualquer
            uma delas ficaria escondida atrás dele. */}
        <rect className="auth-bg-chip" x="338" y="44" width="26" height="26" rx="6" transform="rotate(28 351 57)" />
        <circle className="auth-bg-dot" cx="74" cy="52" r="9" />
        <rect className="auth-bg-ring" x="300" y="826" width="34" height="34" rx="8" transform="rotate(18 317 843)" />
        <circle className="auth-bg-dot auth-bg-dot-amber" cx="96" cy="848" r="7" />
      </svg>

      <div className={`auth-flip${isSignup ? ' is-signup' : ''}`}>
        {/* Alternador do topo: no celular o painel deslizante não cabe (o
            cartão vira uma coluna só), então a troca precisa de um controle
            próprio, sempre visível acima do formulário. */}
        <div className="auth-switch d-md-none" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={!isSignup}
            className={`auth-switch-option${!isSignup ? ' active' : ''}`}
            onClick={() => switchTo('signin')}
          >
            Entrar
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isSignup}
            className={`auth-switch-option${isSignup ? ' active' : ''}`}
            onClick={() => switchTo('signup')}
          >
            Criar conta
          </button>
        </div>

        {/* `inert` no formulário fora de cena: sem isso ele continua na ordem
            de tabulação e o teclado cai em campos invisíveis. */}
        <div className="auth-flip-pane auth-flip-signin" inert={isSignup}>
          <div className="auth-flip-inner">
            <img src="/images/icon-horizontal.png" alt="MyPedidos" className="auth-logo" />
            <h1 className="auth-title">Entrar</h1>
            <LoginForm
              onSubmit={handleLogin}
              loading={loading}
              error={!isSignup ? error : null}
            />
          </div>
        </div>

        <div className="auth-flip-pane auth-flip-signup" inert={!isSignup}>
          <div className="auth-flip-inner">
            <img src="/images/icon-horizontal.png" alt="MyPedidos" className="auth-logo" />
            <h1 className="auth-title">Criar conta</h1>
            <RegisterForm onSubmit={handleRegister} loading={loading} error={isSignup ? error : null} />
          </div>
        </div>

        {/* Painel colorido que desliza. Decorativo no celular, onde some. */}
        <div className="auth-cover d-none d-md-block">
          <div className="auth-cover-slider">
            <div className="auth-cover-panel auth-cover-left">
              <h2 className="auth-cover-title">Que bom te ver de novo!</h2>
              <p className="auth-cover-text">
                Entre para acompanhar os pedidos, ajustar o cardápio e vender pelo PDV.
              </p>
              <button type="button" className="auth-cover-btn" onClick={() => switchTo('signin')}>
                Já tenho conta
              </button>
            </div>

            <div className="auth-cover-panel auth-cover-right">
              <h2 className="auth-cover-title">Primeira vez por aqui?</h2>
              <p className="auth-cover-text">
                Crie a conta do seu estabelecimento e comece a receber pedidos hoje mesmo.
              </p>
              <button type="button" className="auth-cover-btn" onClick={() => switchTo('signup')}>
                Criar conta
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthPage;
